/**
 * SLASH COMMANDS — the registry, the grammar, and what the suggestion list offers.
 *
 * A command is an action typed into the composer instead of clicked: `/stop`, `/model gemini`.
 * Kept out of the view for the same reason `composer-text` is: what counts as a command and what the
 * list should offer are functions of text, testable without a DOM.
 *
 * A command only counts at the very START of a message. Anything else — including a slash followed
 * by a name no command has — is an ordinary message, so "/etc/hosts is missing" still reaches the
 * model.
 *
 * A command never reaches the model and is never written to the transcript. It acts on the panel
 * through `PanelActions`, which the composer implements.
 */
import { t } from '../i18n';
const registry = new Map();
/**
 * Add commands. Returns the undo, the same shape as `registerClientTools`.
 *
 * A later registration under the same name replaces the earlier one, so an app can override a
 * built-in. The undo removes only what this call added, never a replacement made since.
 */
export function registerCommands(...cmds) {
    for (const c of cmds)
        registry.set(c.name, c);
    return () => {
        for (const c of cmds)
            if (registry.get(c.name) === c)
                registry.delete(c.name);
    };
}
export function findCommand(name) {
    return registry.get(name.toLowerCase());
}
const isAvailable = (c, a) => c.available?.(a) ?? true;
/**
 * The command a message IS, if it is one: `/name` alone or `/name argument`, at the very start.
 *
 * Only the shape — whether a command by that name exists is the caller's question. The name must be
 * followed by whitespace or the end, which is what keeps "/etc/hosts" a message.
 */
export function parseCommand(text) {
    const m = /^\/([a-z][a-z-]*)(?:\s+([\s\S]*))?$/i.exec(text.trim());
    return m ? { name: m[1].toLowerCase(), arg: (m[2] ?? '').trim() } : null;
}
/** The most argument rows the list shows. A roster of a hundred models is a search, not a menu. */
const MAX_OPTION_ROWS = 8;
/**
 * What the list should offer for the text in the box. Empty means the list is closed.
 *
 * While the name is being typed, the commands whose name matches — prefix first, then anywhere in
 * the name, each in registry order, so the same text always gives the same list. Once a known
 * command and a space are typed, that command's argument options, filtered the same way.
 */
export function suggestionsFor(text, a) {
    if (!text.startsWith('/'))
        return [];
    const space = text.search(/\s/);
    if (space < 0) {
        const q = text.slice(1).toLowerCase();
        const open = [...registry.values()].filter((c) => isAvailable(c, a));
        const ranked = [...open.filter((c) => c.name.startsWith(q)), ...open.filter((c) => !c.name.startsWith(q) && c.name.includes(q))];
        return ranked.map((c) => ({
            key: c.name,
            label: `/${c.name}`,
            hint: c.arg ? `<${t(c.arg.hintKey)}>` : '',
            detail: t(c.descKey),
            insert: c.arg?.required ? `/${c.name} ` : `/${c.name}`,
            runs: !c.arg?.required,
        }));
    }
    const cmd = findCommand(text.slice(1, space));
    if (!cmd?.arg?.options || !isAvailable(cmd, a))
        return [];
    const q = text.slice(space).trim().toLowerCase();
    return rankOptions(cmd.arg.options(a), q)
        .slice(0, MAX_OPTION_ROWS)
        .map((o) => ({ key: `${cmd.name}:${o.value}`, label: o.label, hint: '', detail: o.detail ?? '', insert: `/${cmd.name} ${o.value}`, runs: true }));
}
/** Options matching `q`: label or value starting with it first, then containing it, each in the
 *  order given. An empty `q` matches everything. */
function rankOptions(options, q) {
    if (!q)
        return [...options];
    const has = (o, f) => f(o.label.toLowerCase()) || f(o.value.toLowerCase());
    const starts = options.filter((o) => has(o, (s) => s.startsWith(q)));
    return [...starts, ...options.filter((o) => !starts.includes(o) && has(o, (s) => s.includes(q)))];
}
/**
 * The option a typed argument means: an exact value or label, else the first that matches as the
 * list would rank it — the row the list shows first, so typing and picking land on the same thing.
 */
export function pickOption(options, arg) {
    const q = arg.trim().toLowerCase();
    if (!q)
        return undefined;
    return options.find((o) => o.value.toLowerCase() === q || o.label.toLowerCase() === q) ?? rankOptions(options, q)[0];
}
//# sourceMappingURL=commands.js.map